import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import TextInput from '../../../../common/inputs/text-input/TextInput';
import DatePicker from '../../../../common/inputs/date-picker/DatePicker';
import Button from '../../../../common/button/Button';

import { ReactComponent as LogoDark } from '../../../../../resources/logos/atp-logo-light.svg';
import { ReactComponent as Cross } from '../../../../../resources/icons/close.svg';

import './personal-information.scss';

const PersonalInformation = ({
  setScreenStep,
  setPersonalInformation,
  organisation,
  register,
  setOrgForScreen,
  personalInformation,
  resetPersonalInformation,
  linkedRegistration,
}) => {
  const [disableFields, setDisableFields] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState('');
  const [organisationExternalReference, setOrganisationExternalReference] = useState(undefined);
  const [tradingName, setTradingName] = useState('');
  const [taxRegistrationNumber, setTaxRegistrationNumber] = useState('');
  const [incorporationDate, setIncorporationDate] = useState(undefined);
  const [importerCode, setImporterCode] = useState('');
  const [webURL, setWebURL] = useState('');
  const [addressLineOne, setAddressLineOne] = useState('');
  const [addressLineTwo, setAddressLineTwo] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [title, setTitle] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [linkedOrganisationBool, setLinkedOrganisationBool] = useState(false);
  const [companyNameEmpty, setCompanyNameEmpty] = useState(false);
  const [regEmpty, setRegEmpty] = useState(false);
  const [countryEmpty, setCountryEmpty] = useState(false);
  const [firstNameEmpty, setFirstNameEmpty] = useState(false);
  const [lastNameEmpty, setLastNameEmpty] = useState(false);

  const save = () => {
    const details = {
      companyName,
      companyRegistrationNumber,
      tradingName,
      taxRegistrationNumber,
      incorporationDate,
      importerCode,
      webURL,
      addressLine1: addressLineOne,
      addressLine2: addressLineTwo,
      city,
      province,
      country,
      postalCode,
      title,
      jobTitle,
      firstName,
      lastName,
      mobile,
      organisationExternalReference,
    };

    setPersonalInformation(details);
  };

  useEffect(() => {
    if (organisation) {
      setDisableFields(true);
    }
  }, [organisation]);

  useEffect(() => {
    if (!disableFields) {
      setCompanyName(personalInformation.companyName ? personalInformation.companyName : '');
      setCompanyRegistrationNumber(
        personalInformation.companyRegistrationNumber ? personalInformation.companyRegistrationNumber : ''
      );
      setTradingName(personalInformation.tradingName ? personalInformation.tradingName : '');
      setTaxRegistrationNumber(
        personalInformation.taxRegistrationNumber ? personalInformation.taxRegistrationNumber : ''
      );
      setImporterCode(personalInformation.importerCode ? personalInformation.importerCode : '');
      setWebURL(personalInformation.webURL ? personalInformation.webURL : '');
      setOrganisationExternalReference(
        personalInformation.organisationExternalReference ? personalInformation.organisationExternalReference : ''
      );
      setAddressLineOne(personalInformation.addressLine1 ? personalInformation.addressLine1 : '');
      setAddressLineTwo(personalInformation.addressLine2 ? personalInformation.addressLine2 : '');
      setCity(personalInformation.city ? personalInformation.city : '');
      setProvince(personalInformation.province ? personalInformation.province : '');
      setCountry(personalInformation.country ? personalInformation.country : '');
      setPostalCode(personalInformation.postalCode ? personalInformation.postalCode : '');

      if (personalInformation.incorporationDate) {
        setIncorporationDate(new Date(personalInformation.incorporationDate));
      }
    } else {
      setCompanyName(organisation.companyName ? organisation.companyName : '');
      setCompanyRegistrationNumber(
        organisation.companyRegistrationNumber ? organisation.companyRegistrationNumber : ''
      );
      setTradingName(organisation.tradingName ? organisation.tradingName : '');
      setTaxRegistrationNumber(organisation.taxRegistrationNumber ? organisation.taxRegistrationNumber : '');
      if (organisation.incorporationDate) {
        setIncorporationDate(new Date(organisation.incorporationDate));
      }
      setImporterCode(organisation.importerCode ? organisation.importerCode : '');
      setWebURL(organisation.webURL ? organisation.webURL : '');
      setOrganisationExternalReference(
        organisation.organisationExternalReference ? organisation.organisationExternalReference : ''
      );
      setAddressLineOne(organisation.addressLine1 ? organisation.addressLine1 : '');
      setAddressLineTwo(organisation.addressLine2 ? organisation.addressLine2 : '');
      setCity(organisation.city ? organisation.city : '');
      setProvince(organisation.province ? organisation.province : '');
      setCountry(organisation.country ? organisation.country : '');
      setPostalCode(organisation.postalCode ? organisation.postalCode : '');
    }
    setTitle(personalInformation.title ? personalInformation.title : '');
    setJobTitle(personalInformation.jobTitle ? personalInformation.jobTitle : '');
    setFirstName(personalInformation.firstName ? personalInformation.firstName : '');
    setLastName(personalInformation.lastName ? personalInformation.lastName : '');
    setMobile(personalInformation.mobile ? personalInformation.mobile : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableFields]);

  useEffect(() => {
    setLinkedOrganisationBool(organisationExternalReference ? true : false);
  }, [organisationExternalReference]);

  const assessAreFieldsValid = () => {
    let areFieldsValid = true;
    if (!companyName && !linkedRegistration) {
      setCompanyNameEmpty(true);
      areFieldsValid = false;
    }

    if (!country && !linkedRegistration) {
      setCountryEmpty(true);
      areFieldsValid = false;
    }

    if (!firstName) {
      setFirstNameEmpty(true);
      areFieldsValid = false;
    }

    if (!lastName) {
      setLastNameEmpty(true);
      areFieldsValid = false;
    }

    return areFieldsValid;
  };

  const getPersonalInformation = () => {
    const personalInfo = {
      companyName,
      companyRegistrationNumber,
      tradingName,
      taxRegistrationNumber,
      incorporationDate,
      importerCode,
      webURL,
      addressLine1: addressLineOne,
      addressLine2: addressLineTwo,
      city,
      province,
      country,
      postalCode,
      title,
      jobTitle,
      firstName,
      lastName,
      mobile,
      organisationExternalReference,
    };
    return personalInfo;
  };

  const handleContinueClick = () => {
    if (!assessAreFieldsValid()) return;

    save();

    if (!linkedRegistration) {
      setScreenStep(3);
      return;
    }

    register(getPersonalInformation());
  };

  const handleRegisterClick = () => {
    if (!assessAreFieldsValid()) return;

    save();
    register(getPersonalInformation());
  };

  const getRegIcon = () => {
    if (disableFields) {
      return (
        <div
          onClick={() => {
            setOrgForScreen(undefined);
            setDisableFields(false);
            resetPersonalInformation();
          }}
        >
          <Cross className="personal-information__cross-icon" />
        </div>
      );
    }
  };

  return (
    <section className="personal-information">
      <LogoDark className="personal-information__logo" />
      <section className="personal-information__container">
        <div className="personal-information__back">
          <p
            onClick={() => {
              save();
              setScreenStep(1);
            }}
          >
            {'<'}Back
          </p>
        </div>

        <section className="personal-information__profile personal-information__card" data-testid="profile-section">
          <section
            className="personal-information__profile__company-information personal-information__card"
            data-testid="company-information-section"
          >
            <h1>Step 2:</h1>
            <h2>Personal Information </h2>
            <h3>(You can always complete this later in your account settings)</h3>
            {!linkedRegistration && (
              <div className="personal-information__profile__company-information__fields">
                <p className="personal-information__section-title">Company Information</p>
                <div className="personal-information__profile__field">
                  <label htmlFor="company-name">Company Name</label>
                  <h4>(Required)</h4>
                  <TextInput
                    name="company-name"
                    disabled={disableFields}
                    value={companyName}
                    error={companyNameEmpty}
                    onChange={({ target }) => {
                      setCompanyNameEmpty(false);
                      setCompanyName(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="company-registration-number">Company Registration No.</label>
                  <TextInput
                    name="company-registration-number"
                    icon={getRegIcon()}
                    value={companyRegistrationNumber}
                    error={regEmpty}
                    onChange={({ target }) => {
                      setRegEmpty(false);
                      setCompanyRegistrationNumber(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="trading-name">Trading Name</label>
                  <TextInput
                    name="trading-name"
                    value={tradingName}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setTradingName(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="vat-number">Vat No.</label>
                  <TextInput
                    name="vat-number"
                    disabled={disableFields}
                    value={taxRegistrationNumber}
                    onChange={({ target }) => {
                      setTaxRegistrationNumber(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="incorporation-date">Incorporation Date</label>
                  <DatePicker
                    name="incorporation-date"
                    value={incorporationDate}
                    disabled={disableFields}
                    onChange={(date) => {
                      setIncorporationDate(date);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="importer-code">Importer Code</label>
                  <TextInput
                    name="importer-code"
                    value={importerCode}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setImporterCode(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="web-url">Web URL</label>
                  <TextInput
                    name="web-url"
                    value={webURL}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setWebURL(target.value);
                    }}
                  />
                </div>
              </div>
            )}
          </section>

          {!linkedRegistration && (
            <section
              className="personal-information__profile__company-contact-information personal-information__card"
              data-testid="company-contact-information-section"
            >
              <div className="personal-information__profile__company-contact-information__fields">
                <p className="personal-information__section-title">Company Contact Information</p>
                <div className="personal-information__profile__field">
                  <label htmlFor="address-line-one">Address Line 1</label>
                  <TextInput
                    name="address-line-one"
                    value={addressLineOne}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setAddressLineOne(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="address-line-two">Address Line 2</label>
                  <TextInput
                    name="address-line-two"
                    value={addressLineTwo}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setAddressLineTwo(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="city">City</label>
                  <TextInput
                    name="city"
                    value={city}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setCity(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="province">Province/Region/State</label>
                  <TextInput
                    name="province"
                    value={province}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setProvince(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="country">Country</label>
                  <h4>(Required)</h4>
                  <TextInput
                    name="country"
                    value={country}
                    error={countryEmpty}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setCountryEmpty(false);
                      setCountry(target.value);
                    }}
                  />
                </div>
                <div className="personal-information__profile__field">
                  <label htmlFor="postal-code">Postal Code</label>
                  <TextInput
                    name="postal-code"
                    value={postalCode}
                    disabled={disableFields}
                    onChange={({ target }) => {
                      setPostalCode(target.value);
                    }}
                  />
                </div>
              </div>
            </section>
          )}

          <section
            className="personal-information__profile__personal-information personal-information__card"
            data-testid="personal-information-section"
          >
            <div className="personal-information__profile__personal-information-fields">
              <p className="personal-information__section-title">Personal Information</p>
              <div className="personal-information__profile__field">
                <label htmlFor="title">Title</label>
                <TextInput
                  name="title"
                  value={title}
                  onChange={({ target }) => {
                    setTitle(target.value);
                  }}
                />
              </div>
              <div className="personal-information__profile__field">
                <label htmlFor="job-title">Job Title</label>
                <TextInput
                  name="job-title"
                  value={jobTitle}
                  onChange={({ target }) => {
                    setJobTitle(target.value);
                  }}
                />
              </div>
              <div className="personal-information__profile__field">
                <label htmlFor="first-name">First Name</label>
                <h4>(Required)</h4>
                <TextInput
                  name="first-name"
                  value={firstName}
                  error={firstNameEmpty}
                  onChange={({ target }) => {
                    setFirstNameEmpty(false);
                    setFirstName(target.value);
                  }}
                />
              </div>
              <div className="personal-information__profile__field">
                <label htmlFor="last-name">Last Name</label>
                <h4>(Required)</h4>
                <TextInput
                  name="last-name"
                  error={lastNameEmpty}
                  value={lastName}
                  onChange={({ target }) => {
                    setLastNameEmpty(false);
                    setLastName(target.value);
                  }}
                />
              </div>
              <div className="personal-information__profile__field">
                <label htmlFor="mobile">Mobile</label>
                <TextInput
                  name="mobile"
                  value={mobile}
                  onChange={({ target }) => {
                    setMobile(target.value);
                  }}
                />
              </div>
            </div>
            <div className="personal-information__button-container">
              {!linkedOrganisationBool && <Button onClick={handleContinueClick}>Continue</Button>}
              {linkedOrganisationBool && <Button onClick={handleRegisterClick}>Register</Button>}
            </div>
          </section>
        </section>

        {!linkedOrganisationBool && (
          <div className="personal-information__skip">
            <p
              onClick={() => {
                if (!assessAreFieldsValid()) {
                  return;
                }
                save();
                setScreenStep(3);
              }}
            >
              Skip{'>'}
            </p>
          </div>
        )}
        {linkedOrganisationBool && <div className="personal-information__skip" />}
      </section>
    </section>
  );
};

PersonalInformation.propTypes = {
  setScreenStep: PropTypes.func.isRequired,
  setPersonalInformation: PropTypes.func.isRequired,
  organisation: PropTypes.shape({}),
  register: PropTypes.func.isRequired,
  setOrgForScreen: PropTypes.func.isRequired,
  personalInformation: PropTypes.shape({}).isRequired,
  resetPersonalInformation: PropTypes.func.isRequired,
  linkedRegistration: PropTypes.bool,
};

PersonalInformation.defaultProps = {
  organisation: undefined,
  linkedRegistration: false,
};
export default PersonalInformation;
